import { css, useTheme } from "@emotion/react";
import React, { KeyboardEvent, useEffect, useState } from "react";

import { ReactComponent as Home } from "../icons/home.svg";
import { ReactComponent as Down } from "../icons/down.svg";
import { ReactComponent as More } from "../icons/more.svg";
import { UpsunTheme } from "../themes";
import { fonts, mediaQueries, pxToRem, weights, z } from "../styles";
import colorsLight from "../styles/colors-light";
import colorsDark from "../styles/colors-dark";
import GridContainer from "./GridContainer";
import GridCol from "./GridCol";
import Button from "./Button";
import { spacing } from "../styles/css-utils";

type JumplinkBarProps = {
  mode: "jumplinks" | "secondary",
  links: {
    href: string
    text: string
  }[],
  cta?: {
    icon: React.ReactElement,
    text: string,
    href: string
  }
  customMargin?: {
    top: "3xs" | "2xs" | "xs" | "s" | "sm" | "m" | "ml" | "l" | "xl" | "2xl" | "3xl" | "4xl"
    bottom: "3xs" | "2xs" | "xs" | "s" | "sm" | "m" | "ml" | "l" | "xl" | "2xl" | "3xl" | "4xl"
  }
  matchActiveParent?: boolean
  matchOverride?: string
};

const JumplinkBar: React.FC<JumplinkBarProps> = ({ 
  mode, 
  links, 
  cta, 
  customMargin, 
  matchActiveParent,
  matchOverride,
  ...props 
}) => {
  const theme = useTheme() as UpsunTheme;

  const [showLinks, setShowLinks] = useState(false);
  const [selectedItem, setSelectedItem] = useState(links[0].text);
  const [showBtn, setShowBtn] = useState(false);

  const containerStyles = css`
    position: sticky;
    z-index: ${z('over-main')};
    top: ${pxToRem(0)};
    padding: ${pxToRem(8)} ${pxToRem(0)};

    background-color: ${theme.name === "light"
      ? colorsLight.CanvasDefault
      : colorsDark.CanvasDefault};

    ${mode === "jumplinks" && `
      border-top: ${pxToRem(1)} solid ${theme.name === "light" ? colorsLight.NeutralBorderWeakDefault : colorsDark.NeutralBorderWeakDefault};
      border-bottom: ${pxToRem(1)} solid ${theme.name === "light" ? colorsLight.NeutralBorderWeakDefault : colorsDark.NeutralBorderWeakDefault};
    `};

    ${mode === "jumplinks" && `
      margin-top: ${pxToRem(spacing.mobile[customMargin ? customMargin.top : "2xl"])};
      margin-bottom: ${pxToRem(spacing.mobile[customMargin ? customMargin.bottom : "3xl"])};
    `};

    ${mediaQueries.md} {
      ${mode === "jumplinks" && `
        margin-top: ${pxToRem(spacing.tablet[customMargin ? customMargin.top : "2xl"])};
        margin-bottom: ${pxToRem(spacing.tablet[customMargin ? customMargin.bottom : "3xl"])};
      `};
    }

    ${mediaQueries.lg} {
      padding: 0;
      ${mode === "jumplinks" && `
        margin-top: ${pxToRem(spacing.desktop[customMargin ? customMargin.top : "2xl"])};
        margin-bottom: ${pxToRem(spacing.desktop[customMargin ? customMargin.bottom : "3xl"])};
      `};
    }
  `;

  const onThisPage = css`
    font-weight: ${weights.bold};

    ${mediaQueries.lg} {
      display: none;
    }
  `;

  const linkContainerStyles = css`
    position: absolute;
    top: calc(100% + ${pxToRem(8)});
    left: 0;

    display: ${showLinks ? `flex` : `none`};
    flex-direction: column;
    width: 100%;

    background: ${theme.name === "light"
      ? colorsLight.NeutralBgDefault
      : colorsDark.NeutralBgDefault};

    padding: ${pxToRem(16)} ${pxToRem(12)};
    gap: ${pxToRem(16)};

    border: ${pxToRem(2)} solid ${theme.name === "light" ? colorsLight.NeutralBorderWeakDefault : colorsDark.NeutralBorderWeakDefault};
    border-radius: ${pxToRem(8)};

    ${mediaQueries.lg} {
      position: relative;
      top: 0;
      border: ${pxToRem(2)} solid transparent;
      border-radius: 0;
      display: flex;
      flex-direction: row;
      align-items: center;
      gap: ${pxToRem(mode === "jumplinks" ? 32 : 8)};
      padding: 0;
      background: none;
    }

    a:focus-visible {
      outline: none;
    }

    a:first-of-type {
      ${mode === "secondary" ? `
        display: none;
      ` : `
        display: flex;
      `}

      ${mediaQueries.lg} {
        display: flex;
      }
    }
  `;

  const secondaryToggleStyles = css`
    padding: ${pxToRem(6)};

    &:active {
      background-color: ${theme.name === "light" ? colorsLight.PrimaryBgActive : colorsDark.PrimaryBgActive};

      .selectedItem {
        color: ${theme.name === "light" ? colorsLight.PrimaryTxtInvActive : colorsDark.PrimaryTxtInvActive};
      }

      .dropdownIcon path {
        fill: ${theme.name === "light" ? colorsLight.PrimaryTxtInvActive : colorsDark.PrimaryTxtInvActive};
      }
    }

    &:hover {
      background-color: ${theme.name === "light" ? colorsLight.PrimaryBgHover : colorsDark.PrimaryBgHover};

      .selectedItem {
        color: ${theme.name === "light" ? colorsLight.PrimaryTxtInvHover : colorsDark.PrimaryTxtInvHover};
      }

      .dropdownIcon path {
        fill: ${theme.name === "light" ? colorsLight.PrimaryTxtInvHover : colorsDark.PrimaryTxtInvHover};
      }
    }

    ${mediaQueries.lg} {
      &:active {
        background-color: transparent;
      }

      &:hover {
        background-color: transparent;
      }
    }
  `;

  const toggleStyles = css`
    position: relative;
    flex: 1;

    border: ${pxToRem(2)} solid
      ${theme.name === "light"
        ? colorsLight.NeutralBorderDefault
        : colorsDark.NeutralBorderDefault};
    border-radius: ${pxToRem(8)};

    //Maybe switch top-bottom to 8 to take border into account
    padding: ${pxToRem(10)};

    background: ${theme.name === "light"
      ? colorsLight.NeutralBgDefault
      : colorsDark.NeutralBgDefault};

    cursor: pointer;

    display: flex;
    align-items: center;
    justify-content: space-between;

    ${mediaQueries.lg} {
      border: none;
      border-radius: 0;
      padding-left: 0;
      background: none;
      cursor: default;
    }

    ${mode === "secondary" && secondaryToggleStyles};
    font-size: unset;
  `;

  const gridColStyles = css`
    display: flex;
    justify-content: space-between;
    align-items: center;

    gap: ${pxToRem(mode === "jumplinks" ? 28 : 4)};
  `;

  const ctaStyles = css`
    margin-left: auto;
    display: none;

    padding: ${pxToRem(8)} ${pxToRem(24)} !important;
    border-radius: ${pxToRem(8)} !important;
    font-size: ${pxToRem(16)} !important;

    ${mediaQueries.lg} {
      display: flex;
      align-items: center;
      gap: ${pxToRem(8)};
    }
  `;

  const dropdownArrow = css`
    display: flex;
    align-items: center;

    ${mediaQueries.lg} {
      display:none;
    }
  `;

  const flipAnimation = css`
    transform: rotate(180deg);
    transform: rotate(${showLinks ? "180deg": "0deg"});
  `;

  const svgStyles = css`
    path {
      fill: ${theme.name === "light" ? colorsLight.NeutralIconWeakDefault : colorsDark.NeutralIconWeakDefault};
    }
  `;

  const selectedItemStyles = css`
    ${mediaQueries.lg} {
      display: none;
    }
  `;

  useEffect(() => {
    if (mode === "secondary" && typeof window !== `undefined`) {
      const location = window.location.pathname;

      links.map((link) => {
        if (matchOverride) {
          // Nested instead of using && on purpose; if there's a matchOverride 
          // set, we don't want it matching the location on some other link
          if (link.href.includes(matchOverride)) {
            setSelectedItem(link.text);
          }
        }
        else if (
          link.href === location ||
          (matchActiveParent && location.includes(link.href))
        ) {
          setSelectedItem(link.text);
        }
      });
    }

    if (mode === "jumplinks") {
      const createObserver = (title : string) => {
        /* eslint-disable no-new */
        return new IntersectionObserver(
          (entries) => {
            if(entries[0].isIntersecting) {
              setSelectedItem(title);
            }
          },
          {
            root: document,
            rootMargin: "-50% 0px -50%",
          }
        );
      };

      const useObserver = (id : string, title : string) => {
        const element = document.querySelector(id) as Element;
        if (element) {
          createObserver(title).observe(element);
        }
      };

      links.map((option) => {
        useObserver(option.href, option.text);
      });

      if (cta) {
        const checkYOffset = () => {
          const scrollY = window?.pageYOffset;

          if (scrollY >= 100) {
            setShowBtn(true);
          } else {
            setShowBtn(false);
          }
        };

        if (window) {
          window.addEventListener("scroll", checkYOffset);
        }
  
        return () => window.removeEventListener("scroll", checkYOffset);
      }
    }
  },[typeof window !== "undefined" && window.pageYOffset]);

  const baseLinkStyles = css`
    display: flex;
    line-height: ${pxToRem(24)};
    text-decoration: none;
    font-family: ${fonts.secondary};
    font-weight: ${weights.medium};
    color: ${theme.name === "light" ? colorsLight.NeutralTxtDefault : colorsDark.NeutralTxtDefault};
    border-radius: ${pxToRem(2)};
    padding: ${pxToRem(4)};

    ${mediaQueries.lg} {
      padding: ${pxToRem(10)};
      border-radius: ${pxToRem(8)};
    }
  `;

  const secondaryLinkStyles = css`
    ${baseLinkStyles};

    &:active, &.active, &:focus {
      background-color: ${theme.name === "light" ? colorsLight.PrimaryBgActive : colorsDark.PrimaryBgActive};
      color: ${theme.name === "light" ? colorsLight.PrimaryTxtInvActive : colorsDark.PrimaryTxtInvActive};
    }

    &:hover {
      background-color: ${theme.name === "light" ? colorsLight.PrimaryBgHover : colorsDark.PrimaryBgHover};
      color: ${theme.name === "light" ? colorsLight.PrimaryTxtInvHover : colorsDark.PrimaryTxtInvHover};
    }

    //For home icon
    path {
      fill: ${theme.name === "light" ? colorsLight.NeutralIconDefault : colorsDark.NeutralIconDefault};
    }

    &:active path, &.active path {
      fill: ${theme.name === "light" ? colorsLight.PrimaryTxtInvActive : colorsDark.PrimaryTxtInvActive};
    }

    &:hover path {
      fill: ${theme.name === "light" ? colorsLight.PrimaryTxtInvHover : colorsDark.PrimaryTxtInvHover};
    }

    ${mediaQueries.lg} {
      background-color: ${theme.name === "light" ? colorsLight.NeutralBgWeakDefault : colorsDark.NeutralBgWeakDefault};
    }
  `;

  const jumpLinkStyles = css`
    ${baseLinkStyles};

    &:active, &.active, &:focus {
      background-color: ${theme.name === "light" ? colorsLight.PrimaryBgActive : colorsDark.PrimaryBgActive};
      color: ${theme.name === "light" ? colorsLight.PrimaryTxtInvActive : colorsDark.PrimaryTxtInvActive};
    }

    &:hover {
      background-color: ${theme.name === "light" ? colorsLight.PrimaryBgHover : colorsDark.PrimaryBgHover};
      color: ${theme.name === "light" ? colorsLight.PrimaryTxtInvHover : colorsDark.PrimaryTxtInvHover};
    }
  `;

  const linkStyles = mode === "jumplinks" ? jumpLinkStyles : secondaryLinkStyles;

  const mobileHomeButtonStyles = css`
    padding: ${pxToRem(10)};
    border-radius: ${pxToRem(8)};
    background-color: ${theme.name === "light" ? colorsLight.NeutralBgWeakDefault : colorsDark.NeutralBgWeakDefault};

    border: ${pxToRem(2)} solid transparent;

    ${mediaQueries.lg} {
      display: none;
    }
  `;

  return (
    <div css={containerStyles} {...props}>
      <GridContainer>
        <GridCol colStart={1} colEnd={13} css={gridColStyles}>
          {mode === "jumplinks" && <div css={onThisPage}>On this page </div>}
          {mode === "secondary" && <a href={links[0].href} css={[linkStyles, mobileHomeButtonStyles]} className={selectedItem === links[0].text ? "active" : ""}><Home />{links[0].text}</a>}
          <div
            css={toggleStyles}
            onClick={(e: any) => {
              setShowLinks(!showLinks);
              if (e.target.tagName === "A") {
                setSelectedItem(e.target.innerText);
              }
            }}
            onKeyDown={(e : KeyboardEvent) => {
              if(e.key === "Enter") {
                setShowLinks(!showLinks);
              }
            }}
            title={selectedItem}
            tabIndex={0}
          >
            <span css={[baseLinkStyles, selectedItemStyles]} className="selectedItem">{selectedItem}</span>
            <div css={dropdownArrow} className="dropdownIcon">{mode === "jumplinks" ? <Down css={[svgStyles, flipAnimation]} /> : <More css={svgStyles} />}</div>
            <div css={linkContainerStyles}>
              {links.map((link, index) => (
                <a href={link.href} css={linkStyles} key={`subnav-link-${link.href}-${link.text}`} className={selectedItem === link.text ? "active" : ""}>
                  {mode === "secondary" && index === 0 && <Home />}
                  {link.text}
                </a>
              ))}
              {cta && mode === "jumplinks" && showBtn && <Button href={cta.href} css={ctaStyles}>{cta.icon}{cta.text}</Button>}
            </div>
          </div>
        </GridCol>
      </GridContainer>
    </div>
  );
};

export default JumplinkBar;
